import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { useTheme } from '../../ui/theme-provider';
import { cn } from '../../../lib/utils';
import { Image, FileText, Megaphone, Sparkles, Library, Copy, Check, Instagram, ListOrdered } from 'lucide-react';
import { getFirestore, collection, query, orderBy, limit, onSnapshot, addDoc, serverTimestamp } from 'firebase/firestore';
import UnifiedSocialCanvas from './UnifiedSocialCanvas';
import ControlPanels from './ControlPanels';
import RSSJobSelector from './RSSJobSelector';
import BlogSocialManager from './BlogSocialManager';
import BlogListSelector from './BlogListSelector';
import { exportUnifiedCanvas, exportAt1080AndUpload } from './unifiedExport';
import { useToast } from '../../../hooks/use-toast';
import { createPreviewExportComparison } from './debugComparison';
import { comprehensiveAlignmentTest } from './comprehensiveTest';
import { Button } from '../../ui/button';
import DiscoveryCreatives from './DiscoveryCreatives';
import AICaptionPanel from './AICaptionPanel';
import ImageLibraryPanel from './ImageLibraryPanel';
import PostQueuePanel from './PostQueuePanel';
import discoveryTemplates from './discoveryTemplates.json';

const SocialMediaManager = () => {
  const { theme } = useTheme();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('rss-jobs');
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [activeDraftId, setActiveDraftId] = useState(null); // tracks which scheduledPost is loaded in the editor
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);

  // Comprehensive alignment test
  const handleComprehensiveTest = async () => {
    try {
      const results = await comprehensiveAlignmentTest('unified-social-canvas');
      toast({
        title: results.success ? "Alignment Test PASSED" : "Alignment Test FAILED",
        description: results.message,
        variant: results.success ? "default" : "destructive"
      });
    } catch (error) {
      toast({
        title: "Test Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  // Debug comparison function
  const handleDebugComparison = async () => {
    try {
      await createPreviewExportComparison('unified-social-canvas');
      toast({
        title: "Debug Export",
        description: "Preview and export images saved for comparison",
      });
    } catch (error) {
      toast({
        title: "Debug Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  // Export function using unified export
  const handleExport = async () => {
    try {
      const result = await exportUnifiedCanvas('unified-social-canvas');
      if (result.success) {
        toast({
          title: "Export Successful",
          description: result.message,
        });
      } else {
        toast({
          title: "Export Failed",
          description: result.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Export Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  // Canvas content state (with better defaults to match reference)
  const [canvasData, setCanvasData] = useState({
    company: 'BUPA DENTAL CARE',
    jobTitle: 'Dental Hygienist',
    location: 'BURTON-ON-TRENT',
    supportingText: '',
    topTitle: 'Now hiring',  // Default top title
    taglineText: 'Create invoices, Track, Manage Expenses and search for new Jobs at Coooly.co.uk',
    ctaText: 'Try Free for 60 Days',
    brandText: 'Coooly',
    postedDate: '',
    salary: ''
  });

  // Display options state (show title by default to match reference)
  const [displayOptions, setDisplayOptions] = useState({
    showChips: true,
    showTitle: true,     // Show "Now hiring" by default
    showSupporting: false,
    showTagline: true,   // Show tagline by default
    showCta: true,       // Show CTA by default
    showBrand: false,
  showLogo: true,
  forceWhiteLogo: true,
  showDate: false,
  showSalary: true,
    chipStyle: 1,
    logoType: 'logo'     // 'logo' for logo.png, 'full_logo' for full_logo.png
  });

  // Typography state (optimized for 400x400px canvas to match TestInsta.jpg)
  const [typography, setTypography] = useState({
    headingSize: 24,  // Much smaller for professional look
    bodySize: 12,     // Small text for chips/body
    padSize: 30,      // Tight padding for compact layout
  logoSize: 80,     // Default logo size
  textColor: '#ffffff' // Default text colour
  });

  // Logo position (manual X/Y controls in the UI)
  const [logoPos, setLogoPos] = useState({ x: 18, y: -10 });

  const handleLogoPositionChange = (field, value) => {
    setLogoPos(prev => ({ ...prev, [field]: value }));
  };

  // Discovery gradient override
  const [discoveryGradient, setDiscoveryGradient] = useState(null);

  // Background image selection (file name from public/Background)
  const [backgroundImage, setBackgroundImage] = useState('/Background/background.png');
  // Default logo: use public asset so preview is automated on load
  const [logoUrl, setLogoUrl] = useState('/full_logo.png'); // Holds the URL of the uploaded logo

  // Available background images from public/Background/index.json
  const [backgrounds, setBackgrounds] = useState([]);
  // AI-generated backgrounds from Firestore socialBackgrounds collection
  const [aiBackgrounds, setAiBackgrounds] = useState([]);
  // Last AI-generated caption + hashtags for the Instagram copy panel
  const [postCaption, setPostCaption] = useState('');
  const [postHashtags, setPostHashtags] = useState('');
  const [captionCopied, setCaptionCopied] = useState(false);

  useEffect(() => {
    fetch('/Background/index.json').then(r => r.json()).then(list => {
      const items = Array.isArray(list) ? list.map(i => (typeof i === 'string' ? i : (i.file || i.name || ''))).filter(Boolean) : [];
      setBackgrounds(items);
    }).catch(() => {});
  }, []);

  // Live-listen to AI-generated backgrounds from Firestore
  useEffect(() => {
    const db = getFirestore();
    const q = query(collection(db, 'socialBackgrounds'), orderBy('generatedAt', 'desc'), limit(50));
    const unsub = onSnapshot(q, (snap) => {
      setAiBackgrounds(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, () => {});
    return unsub;
  }, []);

  // Background image styling controls
  const [backgroundStyle, setBackgroundStyle] = useState({
    // default to smaller size and bottom-left positioning to match requested look
    backgroundSize: 100,
    backgroundPositionX: 50,
    backgroundPositionY: 50,
  // overlay opacity as percentage (0-200)
  overlayOpacity: 60
  });

  const handleContentChange = (field, value) => {
    setCanvasData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDisplayChange = (field, value) => {
    setDisplayOptions(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTypographyChange = (field, value) => {
    setTypography(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleBackgroundChange = (url) => {
    setBackgroundImage(url);
    if (url) {
      // Clear any discovery gradient so the image is not masked
      setDiscoveryGradient(null);
      // Bump overlay to a reasonable default for photos if currently very low
      setBackgroundStyle(prev => ({
        ...prev,
        overlayOpacity: prev.overlayOpacity < 100 ? 160 : prev.overlayOpacity,
      }));
    }
  };

  const handleLogoChange = (file) => {
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoUrl(e.target.result); // Use data URL for export compatibility
        handleDisplayChange('showLogo', true);
      };
      reader.readAsDataURL(file);
    } else {
      setLogoUrl('');
      handleDisplayChange('showLogo', false);
    }
  };

  const handleBackgroundStyleChange = (field, value) => {
    setBackgroundStyle(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleExportAll = async () => {
    try {
      const result = await exportUnifiedCanvas('unified-social-canvas');
      if (result.success) {
        toast({
          title: "Batch Export Successful",
          description: "All designs exported successfully!",
          variant: "success"
        });
      } else {
        toast({
          title: "Batch Export Not Ready",
          description: result.message,
          variant: "warning"
        });
      }
    } catch (error) {
      toast({
        title: "Batch Export Error", 
        description: "Failed to export images. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Apply AI-generated caption to the canvas tagline field and store for Instagram copy panel
  const handleApplyCaption = (caption, hashtags) => {
    setCanvasData(prev => ({ ...prev, taglineText: caption }));
    setDisplayOptions(prev => ({ ...prev, showTagline: true }));
    if (caption) setPostCaption(caption);
    if (hashtags) setPostHashtags(hashtags);
  };

  const handleCopyInstagramPost = () => {
    const full = [postCaption, '', '✨ Try Coooly free for 60 days → coooly.co.uk', '', postHashtags].filter(Boolean).join('\n');
    navigator.clipboard?.writeText(full).then(() => {
      setCaptionCopied(true);
      setTimeout(() => setCaptionCopied(false), 2500);
    });
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplateId(template.id);
    setCanvasData(prev => ({
      ...prev,
      jobTitle: template.mainHeading,
      company: '',
      location: '',
      topTitle: template.topTitle,
      taglineText: template.taglineText,
      ctaText: template.ctaText,
      supportingText: template.supportingText || '',
      salary: '',
    }));
    setDisplayOptions(prev => ({
      ...prev,
      showTitle: true,
      showTagline: true,
      showCta: true,
      showChips: false,
      showSalary: false,
      showSupporting: false,
    }));
    if (template.gradientOverlay) {
      setBackgroundImage(null);
      setBackgroundStyle(prev => ({ ...prev, overlayOpacity: 0 }));
      // Pass gradient via canvasData — handled in render below
      setDiscoveryGradient(template.gradientOverlay);
    }
  };

  // Load a scheduledPost draft into the editor (from PostQueuePanel)
  const handleLoadDraft = (draft) => {
    if (draft.type === 'job' && draft.templateData) {
      // Load as a job post on the rss-jobs canvas
      const td = draft.templateData;
      setCanvasData(prev => ({
        ...prev,
        company: td.company || td.topTitle || '',
        jobTitle: td.jobTitle || td.mainHeading || '',
        location: td.location || '',
        salary: td.salary || '',
        topTitle: td.topTitle || td.company || 'Now Hiring',
        taglineText: td.taglineText || '',
        ctaText: td.ctaText || 'Apply Now',
        supportingText: '',
      }));
      setDisplayOptions(prev => ({
        ...prev,
        showTitle: true, showTagline: true, showCta: true,
        showChips: true, showSalary: !!(td.salary), showSupporting: false,
      }));
      if (draft.backgroundImageUrl) {
        setBackgroundImage(draft.backgroundImageUrl);
        setDiscoveryGradient(null);
        setBackgroundStyle(prev => ({ ...prev, overlayOpacity: 60 }));
      }
      setActiveDraftId(draft.id);
      setActiveTab('rss-jobs');
      toast({ title: '✅ Job draft loaded', description: `${td.jobTitle || 'Job'} in ${td.location || 'UK'}. Adjust then click 📷 Capture for Instagram.` });
    } else {
      // Brand awareness — look up full template for gradientOverlay etc.
      const fullTemplate = discoveryTemplates.find(t => t.id === draft.templateId);
      if (fullTemplate) {
        handleTemplateSelect(fullTemplate);
      } else if (draft.templateData) {
        setCanvasData(prev => ({
          ...prev,
          jobTitle: draft.templateData.mainHeading || '',
          company: '',
          location: '',
          topTitle: draft.templateData.topTitle || '',
          taglineText: draft.templateData.taglineText || '',
          ctaText: draft.templateData.ctaText || '',
          supportingText: '',
          salary: '',
        }));
      }
      if (draft.backgroundImageUrl) {
        setBackgroundImage(draft.backgroundImageUrl);
        setDiscoveryGradient(null);
        setBackgroundStyle(prev => ({ ...prev, overlayOpacity: 60 }));
      }
      setActiveDraftId(draft.id);
      setActiveTab('discovery');
      toast({ title: '✅ Brand draft loaded', description: `"${draft.templateData?.mainHeading || draft.templateId}" loaded. Adjust then click 📷 Capture for Instagram.` });
    }
  };

  // Export at 1080px and upload to Firebase Storage, link to the active draft
  const handleCaptureForInstagram = async () => {
    let draftId = activeDraftId;

    // No draft loaded — create a new queue entry from the current canvas state
    if (!draftId) {
      toast({ title: '📷 Saving to queue...', description: 'Creating a new draft from the current canvas.' });
      try {
        const db = getFirestore();
        const isJob = activeTab === 'rss-jobs';
        const captionText = postCaption || `${canvasData.jobTitle} in ${canvasData.location}. Apply now at coooly.co.uk`;
        const hashtagText = postHashtags || '#dentalhygienist #coooly #dentallife #ukdentist';
        const docRef = await addDoc(collection(db, 'scheduledPosts'), {
          type: isJob ? 'job' : 'discovery',
          rssJobId: isJob ? (selectedJobId || null) : null,
          templateId: !isJob ? (selectedTemplateId || null) : null,
          templateData: {
            company: canvasData.company || '',
            jobTitle: canvasData.jobTitle || '',
            location: canvasData.location || '',
            salary: canvasData.salary || '',
            topTitle: canvasData.topTitle || 'Now Hiring',
            taglineText: canvasData.taglineText || '',
            ctaText: canvasData.ctaText || '',
            supportingText: canvasData.supportingText || '',
            mainHeading: canvasData.jobTitle || '',
          },
          backgroundImageUrl: (backgroundImage && !backgroundImage.startsWith('blob:')) ? backgroundImage : null,
          caption: captionText,
          hashtags: hashtagText,
          fullCaption: `${captionText}\n\n${isJob ? '🔗 Find more dental jobs at coooly.co.uk' : '✨ Try Coooly free for 60 days → coooly.co.uk'}\n\n${hashtagText}`,
          status: 'draft',
          imageUrl: null,
          igMediaId: null,
          createdAt: serverTimestamp(),
          scheduledFor: null,
        });
        draftId = docRef.id;
        setActiveDraftId(draftId);
      } catch (err) {
        console.error('Create draft error:', err);
        toast({ title: '❌ Failed to create draft', description: err.message, variant: 'destructive' });
        return;
      }
    }

    toast({ title: '📷 Capturing...', description: 'Rendering at 1080×1080px. Please wait.' });
    try {
      await exportAt1080AndUpload('unified-social-canvas', draftId);
      toast({ title: '✅ Captured!', description: 'Image saved to queue. Go to the Queue tab to post it.' });
    } catch (err) {
      console.error('Capture for Instagram error:', err);
      toast({ title: '❌ Capture failed', description: err.message, variant: 'destructive' });
    }
  };

  const handleJobSelect = (job) => {
    setSelectedJobId(job.id);
    
    // Extract company name from title (before the colon)
    let extractedCompany = job.company || 'Company Name';
    let cleanJobTitle = job.title || 'Job Title';
    
    if (job.title && job.title.includes(':')) {
      const titleParts = job.title.split(':');
      extractedCompany = titleParts[0].trim(); // Company name before ":"
      cleanJobTitle = titleParts[1] ? titleParts[1].trim() : titleParts[0].trim(); // Job title after ":"
    }

    // Format posted date
    let formattedDate = '';
    if (job.postedAt) {
      try {
        // Handle Firestore timestamp or date string
        const date = job.postedAt.toDate ? job.postedAt.toDate() : new Date(job.postedAt);
        formattedDate = date.toLocaleDateString('en-GB', { 
          day: 'numeric', 
          month: 'short', 
          year: 'numeric' 
        });
      } catch (error) {
        console.error('Error formatting date:', error);
        formattedDate = 'Recently posted';
      }
    }
    
    // Auto-populate canvas with job data
    setCanvasData(prev => ({
      ...prev,
      company: extractedCompany,
      jobTitle: cleanJobTitle,
      location: job.location || 'Location',
      supportingText: job.description || '',
      postedDate: formattedDate,
      salary: job.salary || ''
    }));
  };

  return (
    <div className="container mx-auto" style={{
      background: theme === 'dark' ? '#0d1117' : 'transparent',
      borderRadius: 12, padding: '24px',
      color: theme === 'dark' ? '#e2e8f0' : '#1e293b',
      minHeight: 600
    }}>
      {/* Header with tabs and LLM status */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Megaphone className="h-6 w-6" />
          <span style={{ fontSize: 24, fontWeight: 700 }}>Social Media Management</span>
          <span style={{ fontSize: 15, color: theme === 'dark' ? '#a0aec0' : '#64748b', marginLeft: 8 }}>Create professional social media posts from RSS jobs and blog content</span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => setActiveTab('rss-jobs')}
            style={{
              background: activeTab === 'rss-jobs' ? '#2563eb' : (theme === 'dark' ? '#23263a' : '#f1f5f9'),
              color: activeTab === 'rss-jobs' ? '#fff' : (theme === 'dark' ? '#a0aec0' : '#475569'),
              border: 'none', borderRadius: 6, padding: '8px 18px', fontWeight: 600, fontSize: 15, cursor: 'pointer',
              boxShadow: activeTab === 'rss-jobs' ? '0 2px 8px #2563eb33' : 'none'
            }}
          >
            <Image className="h-4 w-4" style={{ marginRight: 6, verticalAlign: 'middle' }} />
            RSS Jobs
          </button>
          <button
            onClick={() => setActiveTab('blog-posts')}
            style={{
              display: 'none',
              background: activeTab === 'blog-posts' ? '#2563eb' : '#23263a',
              color: activeTab === 'blog-posts' ? '#fff' : '#a0aec0',
              border: 'none', borderRadius: 6, padding: '8px 18px', fontWeight: 600, fontSize: 15, cursor: 'pointer',
              boxShadow: activeTab === 'blog-posts' ? '0 2px 8px #2563eb33' : 'none'
            }}
          >
            <FileText className="h-4 w-4" style={{ marginRight: 6, verticalAlign: 'middle' }} />
            Blog Posts
          </button>
          <button
            onClick={() => { setActiveTab('discovery'); setDiscoveryGradient(null); }}
            style={{
              background: activeTab === 'discovery' ? '#7c3aed' : (theme === 'dark' ? '#23263a' : '#f1f5f9'),
              color: activeTab === 'discovery' ? '#fff' : (theme === 'dark' ? '#a0aec0' : '#475569'),
              border: 'none', borderRadius: 6, padding: '8px 18px', fontWeight: 600, fontSize: 15, cursor: 'pointer',
              boxShadow: activeTab === 'discovery' ? '0 2px 8px #7c3aed55' : 'none'
            }}
          >
            <Sparkles className="h-4 w-4" style={{ marginRight: 6, verticalAlign: 'middle', display: 'inline' }} />
            Discovery
          </button>
          <button
            onClick={() => setActiveTab('image-library')}
            style={{
              background: activeTab === 'image-library' ? '#0d9488' : (theme === 'dark' ? '#23263a' : '#f1f5f9'),
              color: activeTab === 'image-library' ? '#fff' : (theme === 'dark' ? '#a0aec0' : '#475569'),
              border: 'none', borderRadius: 6, padding: '8px 18px', fontWeight: 600, fontSize: 15, cursor: 'pointer',
              boxShadow: activeTab === 'image-library' ? '0 2px 8px #0d948855' : 'none'
            }}
          >
            <Library className="h-4 w-4" style={{ marginRight: 6, verticalAlign: 'middle', display: 'inline' }} />
            Image Library
          </button>
          <button
            onClick={() => setActiveTab('queue')}
            style={{
              background: activeTab === 'queue' ? '#e1306c' : (theme === 'dark' ? '#23263a' : '#f1f5f9'),
              color: activeTab === 'queue' ? '#fff' : (theme === 'dark' ? '#a0aec0' : '#475569'),
              border: 'none', borderRadius: 6, padding: '8px 18px', fontWeight: 600, fontSize: 15, cursor: 'pointer',
              boxShadow: activeTab === 'queue' ? '0 2px 8px #e1306c55' : 'none'
            }}
          >
            <Instagram className="h-4 w-4" style={{ marginRight: 6, verticalAlign: 'middle', display: 'inline' }} />
            Queue
          </button>
        </div>
        {/* LLM status removed — AI captions now powered by Gemini via Cloud Function */}
      </div>

      {/* Image Library — full-width, no canvas needed */}
      {activeTab === 'image-library' && (
        <div style={{ background: '#0d1117', borderRadius: 12, padding: 24, border: '1px solid rgba(255,255,255,0.07)' }}>
          <ImageLibraryPanel
            onSelectImage={(url) => {
              setBackgroundImage(url);
              setActiveTab('rss-jobs');
              toast({ title: 'Background applied', description: 'AI image set as canvas background. Switch to any tab to use it.' });
            }}
          />
        </div>
      )}

      {/* Instagram Queue — full-width */}
      {activeTab === 'queue' && (
        <div style={{ background: '#0d1117', borderRadius: 12, border: '1px solid rgba(255,255,255,0.07)' }}>
          <PostQueuePanel
            onLoadDraft={handleLoadDraft}
            activeDraftId={activeDraftId}
          />
        </div>
      )}

      {/* Main Content: 3 columns, no duplicate LLM controls */}
      {activeTab !== 'image-library' && activeTab !== 'queue' && (
      <div style={{ display: 'flex', gap: 32, alignItems: 'flex-start' }}>
        {/* Left: Selector */}
        <div style={{ minWidth: 320, maxWidth: 340, flex: '0 0 340px' }}>
          {activeTab === 'rss-jobs' && (
            <RSSJobSelector 
              onJobSelect={handleJobSelect}
              selectedJobId={selectedJobId}
            />
          )}
          {activeTab === 'discovery' && (
            <DiscoveryCreatives
              onTemplateSelect={handleTemplateSelect}
              selectedTemplateId={selectedTemplateId}
            />
          )}
          {activeTab === 'blog-posts' && (
              <BlogListSelector onBlogSelect={async (post) => {
              try {
                  // store selected blog for downstream components
                  setSelectedBlog(post);
                  // also keep the global for compatibility with existing generator flow
                  window.__BLOG_SELECTED_POST = post;
                  const mod = await import('./clientGenerator');
                  const extractMarketingMessages = mod.extractMarketingMessages || (mod.default && mod.default.extractMarketingMessages);
                  const clientGen = mod.default || mod;
                  let msgs = null;
                  if (typeof extractMarketingMessages === 'function') {
                    msgs = await extractMarketingMessages({ id: post.id, title: post.title, excerpt: post.excerpt || post.summary || '', body: post.body || '' }, { tone: 'professional' });
                  } else {
                    let fallbackVariants = await (clientGen.generateVariants ? clientGen.generateVariants({ id: post.id, title: post.title, excerpt: post.excerpt || post.summary || '', url: post.url || '' }, 'blog_highlight', { tone: 'professional', length: 'short' }) : []);
                    try { if (clientGen.enforceProblemTitles) fallbackVariants = await clientGen.enforceProblemTitles({ id: post.id, title: post.title, excerpt: post.excerpt || post.summary || '', url: post.url || '' }, fallbackVariants || []); } catch(e) {}
                    msgs = (fallbackVariants || []).map(v => ({ message: v.message, confidence: v.confidence || 0.5 }));
                  }
                  let variants = await (clientGen.generateVariants ? clientGen.generateVariants({ id: post.id, title: post.title, excerpt: post.excerpt || post.summary || '', url: post.url || '' }, 'blog_highlight', { tone: 'professional', length: 'short' }) : []);
                  try { if (clientGen.enforceProblemTitles) variants = await clientGen.enforceProblemTitles({ id: post.id, title: post.title, excerpt: post.excerpt || post.summary || '', url: post.url || '' }, variants || []); } catch(e) {}
                  const merged = (variants || []).slice(0, msgs.length).map((v, i) => ({ ...v, message: msgs[i]?.message || v.message }));
                  window.__BLOG_INITIAL_VARIANT_LIST = merged;
                  window.__BLOG_INITIAL_VARIANT = merged && merged[0] ? merged[0] : (variants && variants[0]) || null;
                  setActiveTab('blog-posts');
                  toast({ title: 'Blog selected', description: `Extracted ${merged.length} marketing messages` });
              } catch (e) {
                console.error('Blog selection generation failed', e);
                toast({ title: 'Generation error', description: 'Failed to generate creative for selected blog', variant: 'destructive' });
              }
            }} />
          )}
        </div>

        {/* Center: Creative workspace (canvas + variant editor) */}
        <div style={{ flex: 1, minWidth: 420, display: 'flex', flexDirection: 'column', gap: 24 }}>
          {activeTab === 'rss-jobs' && (
            <Card style={{ marginBottom: 24, background: '#0d1117', border: '1px solid rgba(255,255,255,0.07)' }}>
              <CardContent>
                <UnifiedSocialCanvas
                  canvasId="unified-social-canvas"
                  mainHeading={canvasData.jobTitle}
                  company={canvasData.company}
                  location={canvasData.location}
                  topTitle={canvasData.topTitle}
                  supportingText={canvasData.supportingText}
                  taglineText={canvasData.taglineText}
                  ctaText={canvasData.ctaText}
                  salary={canvasData.salary}
                  showSalary={displayOptions.showSalary}
                  logoX={logoPos.x}
                  logoY={logoPos.y}
                  backgroundOverlayOpacity={backgroundStyle.overlayOpacity}
                  showChips={displayOptions.showChips}
                  showTopTitle={displayOptions.showTitle}
                  showSupportingText={displayOptions.showSupporting}
                  showTagline={displayOptions.showTagline}
                  showCta={displayOptions.showCta}
                  backgroundImage={backgroundImage}
                  backgroundSize={backgroundStyle.backgroundSize}
                  backgroundPositionX={backgroundStyle.backgroundPositionX}
                  backgroundPositionY={backgroundStyle.backgroundPositionY}
                  headingSize={typography.headingSize}
                  bodySize={typography.bodySize}
                  padSize={typography.padSize}
                  textColor={typography.textColor}
                  showLogo={displayOptions.showLogo}
                  forceWhiteLogo={displayOptions.forceWhiteLogo}
                  logoUrl={logoUrl}
                  logoSize={typography.logoSize}
                />
              </CardContent>
            </Card>
          )}
          {activeTab === 'rss-jobs' && (
            <AICaptionPanel
              type="job"
              data={{ role: canvasData.jobTitle, location: canvasData.location, salary: canvasData.salary, company: canvasData.company }}
              onApply={handleApplyCaption}
            />
          )}
          {/* Instagram post copy panel */}
          {activeTab === 'rss-jobs' && (
            <Card style={{ background: '#111827', border: '1px solid #1e293b' }}>
              <CardContent style={{ paddingTop: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: 600, fontSize: 14, color: '#e5e7eb', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 16 }}>📋</span> Instagram Post Copy
                  </span>
                  <button
                    onClick={handleCopyInstagramPost}
                    disabled={!postCaption}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 5, padding: '6px 14px',
                      borderRadius: 6, border: 'none', cursor: postCaption ? 'pointer' : 'not-allowed',
                      background: captionCopied ? '#16a34a' : '#2563eb', color: '#fff', fontWeight: 600, fontSize: 13,
                    }}
                  >
                    {captionCopied ? <><Check size={13} /> Copied!</> : <><Copy size={13} /> Copy All</>}
                  </button>
                </div>
                <textarea
                  value={[postCaption, 'Visit coooly.co.uk to find dental jobs near you.', postHashtags].filter(Boolean).join('\n\n')}
                  onChange={e => {
                    const parts = e.target.value.split('\n\n');
                    setPostCaption(parts[0] || '');
                  }}
                  placeholder="Generate a caption above to build your Instagram post text here..."
                  rows={6}
                  style={{
                    width: '100%', background: '#1e293b', color: '#e5e7eb', border: '1px solid #334155',
                    borderRadius: 8, padding: '10px 12px', fontSize: 13, fontFamily: 'inherit',
                    resize: 'vertical', lineHeight: 1.6, boxSizing: 'border-box',
                  }}
                />
                <p style={{ fontSize: 11, color: '#6b7280', margin: 0 }}>
                  Edit freely — use Copy All to grab the full post text ready for Instagram.
                </p>
              </CardContent>
            </Card>
          )}
          {activeTab === 'discovery' && (
            <Card style={{ marginBottom: 24, background: '#0d1117', border: '1px solid rgba(255,255,255,0.07)' }}>
              <CardContent>
                <UnifiedSocialCanvas
                  canvasId="unified-social-canvas"
                  mainHeading={canvasData.jobTitle}
                  company={canvasData.company}
                  location={canvasData.location}
                  topTitle={canvasData.topTitle}
                  supportingText={canvasData.supportingText}
                  taglineText={canvasData.taglineText}
                  ctaText={canvasData.ctaText}
                  salary={canvasData.salary}
                  showSalary={false}
                  logoX={logoPos.x}
                  logoY={logoPos.y}
                  backgroundOverlayOpacity={discoveryGradient ? 0 : backgroundStyle.overlayOpacity}
                  showChips={displayOptions.showChips}
                  showTopTitle={displayOptions.showTitle}
                  showSupportingText={displayOptions.showSupporting}
                  showTagline={displayOptions.showTagline}
                  showCta={displayOptions.showCta}
                  backgroundImage={discoveryGradient ? null : backgroundImage}
                  backgroundSize={backgroundStyle.backgroundSize}
                  backgroundPositionX={backgroundStyle.backgroundPositionX}
                  backgroundPositionY={backgroundStyle.backgroundPositionY}
                  gradientOverlay={discoveryGradient || 'linear-gradient(135deg, #4f46e5, #9333ea, #0ea5e9)'}
                  headingSize={typography.headingSize}
                  bodySize={typography.bodySize}
                  padSize={typography.padSize}
                  textColor={typography.textColor}
                  showLogo={displayOptions.showLogo}
                  forceWhiteLogo={displayOptions.forceWhiteLogo}
                  logoUrl={logoUrl}
                  logoSize={typography.logoSize}
                />
              </CardContent>
            </Card>
          )}
          {activeTab === 'discovery' && (
            <AICaptionPanel
              type="discovery"
              data={{ mainHeading: canvasData.jobTitle, taglineText: canvasData.taglineText, ctaText: canvasData.ctaText }}
              onApply={handleApplyCaption}
            />
          )}
          {/* Instagram post copy panel for discovery */}
          {activeTab === 'discovery' && (
            <Card style={{ background: '#111827', border: '1px solid #1e293b' }}>
              <CardContent style={{ paddingTop: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: 600, fontSize: 14, color: '#e5e7eb', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 16 }}>📋</span> Instagram Post Copy
                  </span>
                  <button
                    onClick={handleCopyInstagramPost}
                    disabled={!postCaption}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 5, padding: '6px 14px',
                      borderRadius: 6, border: 'none', cursor: postCaption ? 'pointer' : 'not-allowed',
                      background: captionCopied ? '#16a34a' : '#7c3aed', color: '#fff', fontWeight: 600, fontSize: 13,
                    }}
                  >
                    {captionCopied ? <><Check size={13} /> Copied!</> : <><Copy size={13} /> Copy All</>}
                  </button>
                </div>
                <textarea
                  value={[postCaption, 'Find dental jobs at coooly.co.uk', postHashtags].filter(Boolean).join('\n\n')}
                  onChange={e => {
                    const parts = e.target.value.split('\n\n');
                    setPostCaption(parts[0] || '');
                  }}
                  placeholder="Generate a caption above to build your Instagram post text here..."
                  rows={6}
                  style={{
                    width: '100%', background: '#1e293b', color: '#e5e7eb', border: '1px solid #334155',
                    borderRadius: 8, padding: '10px 12px', fontSize: 13, fontFamily: 'inherit',
                    resize: 'vertical', lineHeight: 1.6, boxSizing: 'border-box',
                  }}
                />
                <p style={{ fontSize: 11, color: '#6b7280', margin: 0 }}>
                  Edit freely — use Copy All to grab the full post text ready for Instagram.
                </p>
              </CardContent>
            </Card>
          )}
          {activeTab === 'blog-posts' && (
            <BlogSocialManager
              initialVariant={window.__BLOG_INITIAL_VARIANT || null}
              selectedBlog={selectedBlog}
            />
          )}
        </div>

        {/* Right: Advanced controls (only for jobs) */}
        <div style={{ minWidth: 340, maxWidth: 380, flex: '0 0 360px' }}>
          {(activeTab === 'rss-jobs' || activeTab === 'discovery') && (
            <ControlPanels
              company={canvasData.company}
              jobTitle={canvasData.jobTitle}
              location={canvasData.location}
              supportingText={canvasData.supportingText}
              topTitle={canvasData.topTitle}
              taglineText={canvasData.taglineText}
              ctaText={canvasData.ctaText}
              postedDate={canvasData.postedDate}
              salary={canvasData.salary}
              showChips={displayOptions.showChips}
              showTitle={displayOptions.showTitle}
              showSupporting={displayOptions.showSupporting}
              showTagline={displayOptions.showTagline}
              showCta={displayOptions.showCta}
              showBrand={displayOptions.showBrand}
              showLogo={displayOptions.showLogo}
              forceWhiteLogo={displayOptions.forceWhiteLogo}
              showDate={displayOptions.showDate}
              showSalary={displayOptions.showSalary}
              logoType={displayOptions.logoType}
              backgroundImage={backgroundImage}
              backgroundSize={backgroundStyle.backgroundSize}
              backgroundPositionX={backgroundStyle.backgroundPositionX}
              backgroundPositionY={backgroundStyle.backgroundPositionY}
              onBackgroundChange={handleBackgroundChange}
              onBackgroundStyleChange={handleBackgroundStyleChange}
              onLogoChange={handleLogoChange}
              onLogoPositionChange={handleLogoPositionChange}
              logoX={logoPos.x}
              logoY={logoPos.y}
              backgrounds={backgrounds}
              aiBackgrounds={aiBackgrounds}
              theme={theme}
              backgroundOverlayOpacity={backgroundStyle.overlayOpacity}
              headingSize={typography.headingSize}
              bodySize={typography.bodySize}
              padSize={typography.padSize}
              textColor={typography.textColor}
              logoSize={typography.logoSize}
              chipStyle={displayOptions.chipStyle}
              onContentChange={handleContentChange}
              onDisplayChange={handleDisplayChange}
              onTypographyChange={handleTypographyChange}
              onExport={handleExport}
              onExportAll={handleExportAll}
              onCaptureForInstagram={handleCaptureForInstagram}
              activeDraftId={activeDraftId}
              onDebugComparison={handleDebugComparison}
              onComprehensiveTest={handleComprehensiveTest}
            />
          )}
        </div>
      </div>
      )}
    </div>
  );
};

export default SocialMediaManager;
